/**
 * WhyTerminal — the LLM-narrated "why" console for the Tailor sampler (FXC-9009).
 *
 * A dark, monospace, auto-scrolling terminal that lives as a COLLAPSIBLE strip
 * under the Explore hero (collapsed by default, so it honours the calm-first
 * two-rooms layout — a "⌗ why" toggle opens it). Each point the cursor reaches
 * — a live solve OR a replay step — appends a line: a prompt-style header
 * (`> α 11.5° · flap 30° · CL 3.41`) then the streaming LLM prose beneath, with
 * a blinking caret while a line is narrating.
 *
 * This is the richer sibling of the autoplay caption TICKER: the ticker stays
 * the big one-line caption; the terminal is the running log. It is READ-ONLY —
 * it explains the sampler's choices, it never influences them.
 *
 * Prod-safe: when the narration route 503s (Ollama unreachable, e.g. the AWS
 * deploy) the header shows a subtle "· offline" pill and the prose falls back to
 * the verbatim reason — never a hard error.
 *
 * Mobile (≤560px): the strip collapses to a bottom-sheet toggle; open, it is a
 * fixed sheet at the bottom. No horizontal scroll at 390px (long prose wraps).
 */
import { useEffect, useMemo, useRef, useState } from "react";

import type { ReplayStep } from "./campaignMath";
import {
  bundleForStep,
  useNarrationStream,
  type NarrationStatus,
} from "../../../api/narration";

/** The active point the terminal is narrating (live solve or replay step). The
 *  ReplayStep already carries the verbatim reasons + rich signals (campaignMath);
 *  only coverage-before needs supplying (the prior iteration's coverage_after). */
export interface WhyPoint {
  step: ReplayStep;
  coverageBefore?: number | null;
}

/** One committed log line (a point whose narration finished or was superseded). */
interface LogLine {
  key: string;
  header: string;
  text: string;
  status: NarrationStatus;
}

/** Prompt-style one-line header for a step: `α 11.5° · flap 30° · CL 3.41`. */
function stepHeader(step: ReplayStep): string {
  const bits = [
    `α ${step.alphaDeg.toFixed(1)}°`,
    `flap ${step.deployDeg.toFixed(0)}°`,
  ];
  if (step.blowing) bits.push(`blow ${step.blowing.toFixed(2)}`);
  if (Number.isFinite(step.mach)) bits.push(`M ${step.mach.toFixed(2)}`);
  if (step.CL != null) bits.push(`CL ${step.CL.toFixed(2)}`);
  else if (step.failed) bits.push("failed");
  return bits.join(" · ");
}

export interface WhyTerminalProps {
  /** The point the cursor is on now, or null when nothing is selected. */
  active: WhyPoint | null;
  /** Controlled open state + toggle (parent owns it so autoplay can auto-open). */
  open: boolean;
  onToggle: (open: boolean) => void;
  /** Mobile bottom-sheet styling (≤560px). Parent decides via a media query. */
  compact?: boolean;
}

export function WhyTerminal({ active, open, onToggle, compact }: WhyTerminalProps) {
  // The request for the ACTIVE line (stable key + grounding bundle).
  const request = useMemo(() => {
    if (!active) return null;
    const { key, bundle } = bundleForStep(active.step, {
      coverageBefore: active.coverageBefore,
    });
    return { key, bundle, header: stepHeader(active.step), reason: active.step.reason };
  }, [active]);

  const { text, status, model } = useNarrationStream(request);

  // The current live line, derived every render. Offline/empty → fall back to
  // the verbatim reason so the log is never blank (honesty on the AWS deploy).
  const liveLine = request
    ? {
        key: request.key,
        header: request.header,
        text: status === "offline" || (status === "done" && !text) ? request.reason : text,
        status,
        reason: request.reason,
      }
    : null;

  // The committed log: a point freezes into a line when the active key changes.
  const [log, setLog] = useState<LogLine[]>([]);
  const lastKeyRef = useRef<string | null>(null);
  // The freshest snapshot per key. On every render we stash the CURRENT line
  // under its own key, so when the key later changes the commit effect can look
  // up the OUTGOING key's last-seen line — independent of render/stream timing.
  const snapshotByKeyRef = useRef<Map<string, Omit<LogLine, "key"> & { reason: string }>>(new Map());
  if (liveLine) {
    snapshotByKeyRef.current.set(liveLine.key, {
      header: liveLine.header,
      text: liveLine.text,
      status: liveLine.status,
      reason: liveLine.reason,
    });
  }

  useEffect(() => {
    const key = request?.key ?? null;
    if (key === lastKeyRef.current) return;
    const prevKey = lastKeyRef.current;
    const prev = prevKey ? snapshotByKeyRef.current.get(prevKey) : null;
    if (prevKey && prev) {
      setLog((cur) => {
        if (cur.length && cur[cur.length - 1]!.key === prevKey) return cur;
        const next = [
          ...cur,
          { key: prevKey, header: prev.header, text: prev.text || prev.reason, status: prev.status },
        ];
        // Cap the log so a long autoplay loop doesn't grow unbounded.
        return next.length > 60 ? next.slice(next.length - 60) : next;
      });
      snapshotByKeyRef.current.delete(prevKey);
    }
    lastKeyRef.current = key;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.key]);

  // Auto-scroll to the newest line while streaming (respect a user who scrolls up).
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const pinnedRef = useRef(true);
  useEffect(() => {
    const el = bodyRef.current;
    if (el && pinnedRef.current) el.scrollTop = el.scrollHeight;
  }, [log, text, open]);
  const onScroll = () => {
    const el = bodyRef.current;
    if (!el) return;
    pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
  };

  const offline = status === "offline";
  const liveText = liveLine ? liveLine.text : "";

  return (
    <div
      className="hlw-why-term"
      data-open={open ? "1" : "0"}
      data-compact={compact ? "1" : "0"}
      data-testid="hlw-why-terminal"
    >
      <button
        type="button"
        className="hlw-why-toggle"
        onClick={() => onToggle(!open)}
        aria-expanded={open}
        data-testid="hlw-why-toggle"
        title="LLM-narrated 'why' log — the sampler explains each point it picks"
      >
        <span className="glyph">⌗</span>
        <span>why</span>
        {offline ? (
          <span className="hlw-why-pill" data-testid="hlw-why-offline">· offline</span>
        ) : model && open ? (
          <span className="hlw-why-model">· {model}</span>
        ) : null}
        <span className="chev" aria-hidden>{open ? "▾" : "▸"}</span>
      </button>

      {open ? (
        <div
          className="hlw-why-body"
          ref={bodyRef}
          onScroll={onScroll}
          data-testid="hlw-why-body"
          role="log"
          aria-live="polite"
        >
          {log.length === 0 && !active ? (
            <div className="hlw-why-empty">
              # narration idle — run a fill or replay to watch the sampler explain itself
            </div>
          ) : null}
          {log.map((line) => (
            <div key={line.key} className="hlw-why-line" data-testid="hlw-why-line">
              <div className="hd">
                <span className="prompt">&gt;</span> {line.header}
              </div>
              <div className="prose">{line.text || line.header}</div>
            </div>
          ))}
          {active && liveLine ? (
            <div className="hlw-why-line is-active" data-testid="hlw-why-active">
              <div className="hd">
                <span className="prompt">&gt;</span> {liveLine.header}
              </div>
              <div className="prose">
                {liveText}
                {status === "streaming" ? <span className="caret" aria-hidden /> : null}
                {status === "streaming" && !liveText ? (
                  <span className="narrating">narrating…</span>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
